type FlagVariable =
    | {type: "string"; defaultValue: string}
    | {type: "boolean"; defaultValue: boolean}
    | {type: "number"; defaultValue: number}
    | {type: "json"; defaultValue: Record<string, unknown>};

type FlagVariables = Record<string, FlagVariable>;

export function createFlag<const T extends readonly string[], V extends FlagVariables>(
    key: string,
    options: {variations: T; fallbackVariation: T[number]; variables?: V},
): {
    key: string;
    variations: {[K in T[number]]: K};
    variables: V;
    fallbackVariation: T[number];
    hasFallback: true;
};

export function createFlag<const T extends readonly string[], V extends FlagVariables>(
    key: string,
    options: {variations: T; fallbackVariation?: never; variables?: V},
): {
    key: string;
    variations: {[K in T[number]]: K};
    variables: V;
    fallbackVariation: undefined;
    hasFallback: false;
};

export function createFlag<V extends FlagVariables>(
    key: string,
    options: {fallbackVariation: "on" | "off"; variables?: V},
): {
    key: string;
    variations: {on: "on"; off: "off"};
    variables: V;
    fallbackVariation: "on" | "off";
    hasFallback: true;
};

export function createFlag<V extends FlagVariables>(
    key: string,
    options?: {fallbackVariation?: never; variables?: V},
): {
    key: string;
    variations: {on: "on"; off: "off"};
    variables: V;
    fallbackVariation: undefined;
    hasFallback: false;
};

/**
 * Creates a feature flag configuration object with typed variations.
 *
 * @template T - A readonly array type of string literals representing the flag variations
 * @param key - The unique identifier for the feature flag
 * @param options - Configuration for the flag
 * @param options.variations - Array of variation names for the flag. Defaults to ['on', 'off'] if not provided
 * @param options.fallbackVariation - Optional fallback variation to use when flag evaluation fails. If not provided, decide() will return null when flag doesn't exist in Optimizely
 * @returns An object containing the flag key, typed variations object, fallback variation (or undefined), and hasFallback boolean
 *
 * @example
 * ```typescript
 * // With fallback - variationKey will never be null
 * const flagWithFallback = createFlag('my-feature', {
 *   variations: ['variant-a', 'variant-b', 'control'],
 *   fallbackVariation: 'control'
 * });
 *
 * // Without fallback - variationKey can be null
 * const flagNoFallback = createFlag('my-feature', {
 *   variations: ['variant-a', 'variant-b', 'control']
 * });
 * ```
 */
export function createFlag<
    const T extends readonly string[],
    V extends FlagVariables = Record<string, never>,
>(key: string, options?: {variations?: T; fallbackVariation?: string; variables?: V}) {
    const variations = (options?.variations ?? ["on", "off"]) as
        | T
        | readonly ["on", "off"];
    const variationsObject = Object.fromEntries(
        variations.map((v: string) => [v, v]),
    ) as {[K in (typeof variations)[number]]: K};

    const hasFallback = options?.fallbackVariation !== undefined;

    return {
        key,
        variations: variationsObject,
        variables: options?.variables ?? ({} as V),
        fallbackVariation: options?.fallbackVariation as
            | (typeof variations)[number]
            | undefined,
        hasFallback: hasFallback as typeof hasFallback,
    };
}
